/**
 * Deep Link Handler for Smartie AI
 *
 * Enables triggering predefined prompts via HTML data attributes.
 * Usage: <a href="#" data-smartie-prompt="template_id">Click me</a>
 */

import { fetchQuickActionTemplate } from "../services/apiClientV2";
import { useGuidedFlowStore, useUIStore, useToastStore } from "@store";
import { trackEvent } from "../services/analyticsService";
import { logDebug, logError, logInfo } from "./logging";

const DATA_ATTRIBUTE = "data-smartie-prompt";

let isInitialized = false;
let pendingConfirmation: {
  templateId: string;
  resolve: (proceed: boolean) => void;
} | null = null;

/**
 * Find the closest element with the data-smartie-prompt attribute
 */
function findPromptElement(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement)) return null;
  return target.closest(`[${DATA_ATTRIBUTE}]`) as HTMLElement | null;
}

/**
 * Handle click on an element with data-smartie-prompt
 */
async function handlePromptClick(templateId: string): Promise<void> {
  logInfo("[deepLinkHandler] Deep link clicked for template:", templateId);

  const guidedFlowStore = useGuidedFlowStore.getState();
  const uiStore = useUIStore.getState();
  const toastStore = useToastStore.getState();

  // Track the deep link click
  trackEvent("prompt_deep_link_clicked", {
    template_id: templateId,
    source_url: window.location.href,
    had_active_flow: guidedFlowStore.status !== "idle",
  });

  // Check if there's an active flow
  if (guidedFlowStore.status !== "idle" && guidedFlowStore.activeFlow) {
    logDebug("[deepLinkHandler] Active flow detected, showing confirmation");

    // Show confirmation toast with actions
    const shouldProceed = await showFlowInterruptConfirmation(templateId);
    if (!shouldProceed) {
      logDebug("[deepLinkHandler] User chose to keep current flow");
      return;
    }

    // User confirmed, abandon the current flow
    guidedFlowStore.abandonFlow();
  }

  // Fetch the template
  try {
    logDebug("[deepLinkHandler] Fetching template:", templateId);
    const template = await fetchQuickActionTemplate(templateId);

    if (!template) {
      throw new Error("Template not found");
    }

    logDebug("[deepLinkHandler] Template fetched successfully:", template.title);

    // Open the widget (for widget mode) or navigate (for fullpage mode)
    const isFullpage = document.getElementById("smartie-fullpage-root") !== null;

    if (!isFullpage) {
      // Widget mode: open to sidebar
      uiStore.openChatToSidebar();
    }

    // Navigate to chat view (in case we're on welcome screen in fullpage)
    uiStore.navigate("chat");

    // Start the guided flow with the template
    // Small delay to ensure UI is ready
    setTimeout(() => {
      useGuidedFlowStore.getState().startFlow(template);
      logInfo("[deepLinkHandler] Guided flow started for:", template.title);
    }, 100);
  } catch (error) {
    logError("[deepLinkHandler] Error handling deep link:", error);

    // Show user-friendly error toast
    const errorMessage =
      error instanceof Error && error.message.includes("404")
        ? "Șablonul nu a fost găsit"
        : "Eroare la încărcarea șablonului. Încearcă din nou.";

    toastStore.addToast({
      type: "error",
      message: errorMessage,
      action: {
        label: "Reîncearcă",
        onClick: () => handlePromptClick(templateId),
      },
    });
  }
}

/**
 * Show a confirmation dialog when interrupting an active flow
 * Returns a promise that resolves to true if user wants to proceed, false otherwise
 */
function showFlowInterruptConfirmation(templateId: string): Promise<boolean> {
  return new Promise((resolve) => {
    const toastStore = useToastStore.getState();

    // Store pending confirmation for potential external resolution
    pendingConfirmation = { templateId, resolve };

    // Create confirmation toast with two actions
    const toastId = toastStore.addToast({
      type: "warning",
      message: "Ai un flux în curs. Vrei să îl înlocuiești?",
      action: {
        label: "Da, începe noul flux",
        onClick: () => {
          pendingConfirmation = null;
          toastStore.removeToast(toastId);
          resolve(true);
        },
      },
    });

    // Auto-resolve to false after toast dismisses (5 seconds)
    setTimeout(() => {
      if (pendingConfirmation?.templateId === templateId) {
        pendingConfirmation = null;
        resolve(false);
      }
    }, 5500); // Slightly longer than toast auto-dismiss
  });
}

/**
 * Global click handler for deep links
 */
function globalClickHandler(event: MouseEvent): void {
  const promptElement = findPromptElement(event.target);

  if (!promptElement) return;

  const templateId = promptElement.getAttribute(DATA_ATTRIBUTE);

  if (!templateId) {
    logError("[deepLinkHandler] Element has data attribute but no value");
    return;
  }

  // Prevent default link behavior
  event.preventDefault();
  event.stopPropagation();

  // Handle the prompt click
  handlePromptClick(templateId).catch((error) => {
    logError("[deepLinkHandler] Unhandled error in handlePromptClick:", error);
  });
}

/**
 * Initialize the deep link handler
 * Should be called once when the app mounts
 */
export function initDeepLinkHandler(): void {
  if (isInitialized) {
    logDebug("[deepLinkHandler] Already initialized, skipping");
    return;
  }

  logInfo("[deepLinkHandler] Initializing deep link handler");

  // Use capture phase to intercept clicks before other handlers
  document.addEventListener("click", globalClickHandler, true);

  isInitialized = true;
}

/**
 * Programmatically trigger a prompt by template ID
 * Useful for other parts of the app that want to use the same flow
 */
export async function triggerPromptByTemplateId(
  templateId: string,
): Promise<void> {
  return handlePromptClick(templateId);
}

